import { ReactElement, ReactNode } from 'react';
import { Chip } from '@mui/material';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import ActionButton from '@components/ActionButton';
import RefreshIcon from '@images/refresh.svg?react';
import { StyledFilterSectionWrap } from '../FilterDrawer/FilterDrawer.styles';
import { sxStyles } from '../FilterDrawer/FilterDrawer.styles';
import { CheckboxOption } from '@interfaces';
import { MajorSearchFilters } from '@common-types';

interface SelectedFilter extends CheckboxOption {
  key: keyof MajorSearchFilters;
  icon?: ReactElement;
}

interface FilterSectionProps {
  title?: string;
  resultCount?: number;
  isLoading?: boolean;
  selectedFilters: SelectedFilter[];
  onDeleteFilter: (filter: SelectedFilter) => void;
  onResetFilters: () => void;
  children: ReactNode;
}

export const FilterSection = ({
  title = 'Filter by:',
  resultCount,
  isLoading,
  selectedFilters,
  onDeleteFilter,
  onResetFilters,
  children,
}: FilterSectionProps) => {
  return (
    <StyledFilterSectionWrap>
      <CoreTypography sx={sxStyles.filterSectionTitle} component="div" variant="h4">
        {title}
      </CoreTypography>

      {resultCount !== undefined && (
        <CoreTypography data-testid="resultCount" sx={sxStyles.filterSectionTitle} component="div">
          {isLoading ? 'Searching...' : `${resultCount} results`}
        </CoreTypography>
      )}

      <div data-testid="filterChips">
        {selectedFilters.map((filter) => (
          <CoreTooltip key={`${filter.value}${filter.key}`} title={filter.label}>
            <Chip
              sx={sxStyles.selectedValue}
              icon={filter.icon}
              variant={filter.icon ? 'outlined' : 'filled'}
              label={filter.label}
              color="primary"
              onDelete={() => onDeleteFilter(filter)}
            />
          </CoreTooltip>
        ))}
      </div>

      <CoreBox>
        {selectedFilters.length > 0 && (
          <ActionButton
            data-testid="clearAllFilters"
            sx={sxStyles.clearBtn}
            type="button"
            onClick={onResetFilters}
            variant="text"
          >
            Clear all Filters
            <RefreshIcon />
          </ActionButton>
        )}
      </CoreBox>

      {children}
    </StyledFilterSectionWrap>
  );
};
